import {
  Field,
  FieldDescription,
  FieldLabel,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {useEscalationVariants} from '@/api/hooks';
import type {EscalationVariant} from '@/api/escalation';

/// A P0 alert is mandatory to tag with an escalation variant, and when it spans multiple verticals
/// (policy violations) each one needs its own variant selected - a variant covering "Violent Extremism"
/// doesn't say anything about a bundled "CSAM" submission. Only enforced for a vertical that actually
/// has catalog entries to choose from - one with none configured yet must not block alert creation.
/// This returns the verticals still missing one.
export function getPolicyViolationsMissingVariant(
  policyViolations: string[],
  options: EscalationVariant[],
  selected: Set<string>
): string[] {
  return policyViolations.filter((policyViolation) => {
    const optionsForVertical = options.filter(
      (option) => option.policy_violation === policyViolation
    );
    return (
      optionsForVertical.length > 0 &&
      !optionsForVertical.some((option) => selected.has(option.name))
    );
  });
}

/// Shared by CreateP0AlertDialog and P0AlertsWorkspace's amend flow - one required, per-vertical
/// escalation-variant multi-select. An alert can carry more than one variant per vertical (e.g. a
/// bundle spanning both "Violent Extremism" and "CSAM"), so each vertical's section is itself a
/// multi-select, not a single choice.
export function EscalationVariantPicker({
  policyViolations,
  value,
  onChange
}: {
  readonly policyViolations: string[];
  readonly value: Set<string>;
  readonly onChange: (value: Set<string>) => void;
}) {
  const {data: allVariants = []} = useEscalationVariants();

  if (policyViolations.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {policyViolations.map((policyViolation) => {
        const options = allVariants.filter((v) => v.policy_violation === policyViolation);
        // Each vertical's group only owns its own options, so a change replaces exactly those names
        // in the shared set and leaves every other vertical's selection as it was.
        const ownNames = new Set(options.map((v) => v.name));
        const selectedHere = options.filter((v) => value.has(v.name)).map((v) => v.name);

        return (
          <Field key={policyViolation}>
            <FieldLabel>
              Escalation Variant — {policyViolation}
              {options.length > 0 ? <span className="text-destructive-ink">*</span> : null}
            </FieldLabel>
            {options.length === 0 ? (
              <FieldDescription>
                No escalation variants are configured for this policy violation - none required.
              </FieldDescription>
            ) : (
              <ToggleGroup
                multiple
                variant="outline"
                size="sm"
                className="flex-wrap"
                value={selectedHere}
                onValueChange={(next) => {
                  const merged = new Set([...value].filter((name) => !ownNames.has(name)));
                  for (const name of next) {
                    merged.add(name);
                  }

                  onChange(merged);
                }}>
                {options.map((v) => (
                  <ToggleGroupItem key={v.id} value={v.name}>
                    {v.name}
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            )}
          </Field>
        );
      })}
    </div>
  );
}
